"use client";

import { useApp } from "@/context/AppContext";
import { useMemo, useState, useEffect, useCallback } from "react";
import {
  eachDayOfInterval,
  format,
  isSameDay,
  subDays,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./common/CommonHeader";
import { MetricsGrid } from "./dashboard/MetricsGrid";
import { UpcomingAppointments } from "./dashboard/UpcomingAppointments";
import { TodayBookingsCard } from "./dashboard/TodayBookingsCard";
import { StatusBreakdownCard } from "./dashboard/StatusBreakdownCard";
import { CashByTeamCard } from "./dashboard/CashByTeamCard";
import { MonthlySalesChart } from "./dashboard/MonthlySalesChart";
import {
  TodayStats,
  CashByTeam,
  TodayRevenue,
  MonthlySalesData,
  dashboardService,
} from "@/services/dashboardService";
import { useAppointments } from "@/context/AppointmentContext";

export function Dashboard() {
  const navigate = useNavigate();
  const {
    appointments,
    customers,
    subscriptions,
    invoices,
    vehicles,
    staff,
  } = useApp();
  const { refreshAppointments } = useAppointments();

  // State for charts
  const [salesPeriod, setSalesPeriod] = useState<"weekly" | "monthly">(
    "monthly",
  );

  // State for API data
  const [todayStats, setTodayStats] = useState<TodayStats | null>(null);
  const [cashByTeam, setCashByTeam] = useState<CashByTeam[]>([]);
  const [salesData, setSalesData] = useState<MonthlySalesData[]>([]);
  const [todayRevenue, setTodayRevenue] = useState<TodayRevenue | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data from backend APIs
  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsResult, teamResult, salesResult, revenueResult] = await Promise.all([
        dashboardService.getTodayStats(),
        dashboardService.getCashByTeam(),
        dashboardService.getMonthlySales(salesPeriod),
        dashboardService.getTodayRevenue(),
      ]);
      
      setTodayStats(statsResult);
      setCashByTeam(teamResult);
      setSalesData(salesResult);
      setTodayRevenue(revenueResult);
    } catch (err: any) {
      console.error("Error fetching dashboard data:", err);
      setError(err?.message || "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, [salesPeriod]);

  // Fetch dashboard data on mount and when salesPeriod changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Also refresh appointments on mount to ensure we have data
  useEffect(() => {
    if (appointments.length === 0) {
      refreshAppointments();
    }
  }, []);

  // Refetch sales data when period changes
  const handlePeriodChange = useCallback(async (period: "weekly" | "monthly") => {
    setSalesPeriod(period);
    try {
      const salesResult = await dashboardService.getMonthlySales(period);
      setSalesData(salesResult);
    } catch (err) {
      console.error("Error fetching sales data:", err);
    }
  }, []);

  // Calculate only the metrics that are not fetched from API
  const localData = useMemo(() => {
    const today = new Date();

    // --- Other Metrics ---
    const activeSubsCount = subscriptions.filter(
      (sub) => sub.status === "active",
    ).length;

    // Todays appointments (pending/confirmed on current calendar day)
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

    const todaysAppointments = appointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return (
          aptDate >= startOfToday &&
          aptDate < startOfTomorrow &&
          (apt.status === "PENDING" || apt.status === "CONFIRMED")
        );
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 10);

    return {
      activeSubsCount,
      todaysAppointments,
      totalCustomers: customers.length,
      vehiclesCount: vehicles.length,
    };
  }, [appointments, customers, subscriptions, invoices, vehicles]);

  // Default values for API data when loading or null
  const defaultTodayStats: TodayStats = {
    totalBookings: 0,
    bookingsByType: { online: 0, offline: 0, subscription: 0 },
    statusCounts: {
      completed: 0,
      assigned: 0,
      inProgress: 0,
      notCompleted: 0,
      postponed: 0,
      cancelled: 0,
      pending: 0,
      confirmed: 0,
    },
  };

  const defaultTodayRevenue: TodayRevenue = {
    collected: 0,
    pending: 0,
    totalExpected: 0,
  };

  // Use API data or defaults
  const effectiveTodayStats = todayStats || defaultTodayStats;
  const effectiveTodayRevenue = todayRevenue || defaultTodayRevenue;

  // Helper for status breakdown dictionary for MetricsGrid
  const todayStatsDict: Record<string, number> = {
    scheduled: effectiveTodayStats.totalBookings,
    ...effectiveTodayStats.statusCounts,
  };

  return (
    <div className="space-y-8 max-w-[1600px]">
      {/* Header */}
      <CommonHeader
        title="Dashboard"
        description="Welcome to WeClean Car Wash Management"
        onClick={() => navigate("/appointments")}
        buttonText="New Appointment"
      />

      {/* Metrics Grid */}
      <MetricsGrid
        todayRevenue={effectiveTodayRevenue}
        todayAppointments={effectiveTodayStats.totalBookings}
        todayStats={todayStatsDict}
        totalCustomers={localData.totalCustomers}
        vehiclesCount={localData.vehiclesCount}
        activeSubscriptions={localData.activeSubsCount}
      />

      {/* Today's Bookings and Status Breakdown */}
      <div className="grid gap-6 lg:grid-cols-2">
        <TodayBookingsCard stats={effectiveTodayStats} loading={isLoading} />
        <StatusBreakdownCard stats={effectiveTodayStats} loading={isLoading} />
      </div>

      {/* Monthly Sales Chart and Cash by Team */}
      <div className="grid gap-6 lg:grid-cols-7">
        <MonthlySalesChart
          data={salesData}
          period={salesPeriod}
          onPeriodChange={handlePeriodChange}
          loading={isLoading}
        />
        <div className="lg:col-span-3">
          <CashByTeamCard data={cashByTeam} loading={isLoading} />
        </div>
      </div>

      {/* Upcoming Appointments */}
      <UpcomingAppointments
        appointments={localData.todaysAppointments}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </div>
  );
}
